import { useMemo, useCallback } from 'react';
import {
  AgentMarketPlaceItemWithFilter,
  SortAgentsField,
  SortAgentsFieldsOrder,
} from '../../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

// Custom hook for agent filtering and pagination
function useAgentFiltering(
  agentList: AgentMarketPlaceItemWithFilter[],
  searchValue: string,
  selectedCategoryFilters: string[],
  selectedRoleFilters: string[],
  selectedCloudProviderFilters: string[],
  page: number,
  itemsPerPage: number,
  sortAgentsField: SortAgentsField | '',
  sortAgentFieldsOrder: SortAgentsFieldsOrder,
  goldAgentIds: string[] = [],
  showOnlyGoldRating: boolean = false
) {
  const categoryList = useMemo(() => Array.from(new Set(agentList.map((item) => item.category))), [agentList]);
  const rolesList = useMemo(() => Array.from(new Set(agentList.map((item) => item.role))), [agentList]);
  const cloudProviderList = useMemo(
    () => Array.from(new Set(agentList.map((item) => item.cloud_provider))),
    [agentList]
  );

  const filteredAgents = useMemo(
    () => {
      const filtered = agentList.filter((agent) => {
        //search functionality to include name, cloud provider, tech_stack, and owners
        const matchesName = agent.name.toLowerCase().includes(searchValue.toLowerCase());
        const matchesCloudProvider = agent.cloud_provider.toLowerCase().includes(searchValue.toLowerCase());

        // Check if any tech_stack item contains the search term
        const matchesTechStack =
          agent.tech_stack.some((tech) => tech.toLowerCase().includes(searchValue.toLowerCase())) || false;

        // Check if any owner name or email contains the search term
        const matchesOwners =
          agent.owners.some(
            (owner) =>
              owner.name.toLowerCase().includes(searchValue.toLowerCase()) ||
              owner.email.toLowerCase().includes(searchValue.toLowerCase())
          ) || false;

        const matchesSearchTerm = matchesName || matchesCloudProvider || matchesTechStack || matchesOwners;

        // No filters selected - show all agents
        if (
          selectedCategoryFilters.length === 0 &&
          selectedRoleFilters.length === 0 &&
          selectedCloudProviderFilters.length === 0
        ) {
          return matchesSearchTerm;
        }

        // Logic for matching category, role, and cloud providers when selected
        const categoryMatch = selectedCategoryFilters.length === 0 || selectedCategoryFilters.includes(agent.category);
        const roleMatch = selectedRoleFilters.length === 0 || selectedRoleFilters.includes(agent.role);
        const cloudProviderMatch =
          selectedCloudProviderFilters.length === 0 || selectedCloudProviderFilters.includes(agent.cloud_provider);

        // Return true only if both category and role conditions are satisfied
        return matchesSearchTerm && categoryMatch && roleMatch && cloudProviderMatch;
      });

      // Apply gold rating filter if checkbox is checked
      if (showOnlyGoldRating) {
        return filtered.filter(agent => goldAgentIds.includes(agent._id));
      }

      return filtered;
    },
    [agentList, searchValue, selectedCategoryFilters, selectedRoleFilters, selectedCloudProviderFilters, showOnlyGoldRating, goldAgentIds]
  );

  // Helper functions to determine agent capabilities
  const isAgentDownloadable = useCallback((agent: AgentMarketPlaceItemWithFilter) => {
    return !(
      agent.isPlatformAgent ||
      agent.isLive === false ||
      (agent.custom_ui === 'external' && (!agent.download_url || agent.download_url.trim() === ''))
    );
  }, []);

  const isAgentChattable = useCallback((agent: AgentMarketPlaceItemWithFilter) => {
    return !(
      agent.isLive === false ||
      agent.downloadable_only === true ||
      (agent.custom_ui === 'external' && (agent.ui_url === '' || agent.ui_url === null))
    );
  }, []);

  const getAgentPriority = useCallback((agent: AgentMarketPlaceItemWithFilter) => {
    const downloadable = isAgentDownloadable(agent);
    const chattable = isAgentChattable(agent);

    if (downloadable && chattable) {
      return 1; // Highest priority: Both download and chat available
    } else if (chattable) {
      return 2; // Second priority: Chat only
    } else if (downloadable) {
      return 3; // Third priority: Download only
    } else {
      return 4; // Lowest priority: Neither download nor chat
    }
  }, [isAgentDownloadable, isAgentChattable]);

  // Apply priority-based sorting first, then additional sorting
  const sortedAgents = useMemo(() => {
    // If Certified_Agent sort is selected, show only gold agents
    if (sortAgentsField === SortAgentsField.Certified_Agent) {
      const goldAgents = filteredAgents.filter(agent => goldAgentIds.includes(agent._id));
      
      // Sort gold agents by priority
      return [...goldAgents].sort((a, b) => {
        const priorityA = getAgentPriority(a);
        const priorityB = getAgentPriority(b);
        return priorityA - priorityB;
      });
    }

    // First, sort filtered agents by priority
    const prioritySorted = [...filteredAgents].sort((a, b) => {
      const priorityA = getAgentPriority(a);
      const priorityB = getAgentPriority(b);
      return priorityA - priorityB;
    });

    // If no additional sorting field is selected, return priority-sorted agents
    if (!sortAgentsField || !sortAgentFieldsOrder) {
      return prioritySorted;
    }

    // Apply secondary sorting within each priority group
    const sortFunctions = {
      [SortAgentsField.Name]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      [SortAgentsField.Owner]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) => {
        const ownerA = a.owners.length > 0 ? a.owners[0].name : '';
        const ownerB = b.owners.length > 0 ? b.owners[0].name : '';
        return ownerA.toLowerCase().localeCompare(ownerB.toLowerCase());
      },
      [SortAgentsField.Cloud_Provider]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.cloud_provider.toLowerCase().localeCompare(b.cloud_provider.toLowerCase()),
      [SortAgentsField.Role]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.role.toLowerCase().localeCompare(b.role.toLowerCase()),
      [SortAgentsField.Category]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.category.toLowerCase().localeCompare(b.category.toLowerCase()),
      [SortAgentsField.Date]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        new Date(a.last_updated).getTime() - new Date(b.last_updated).getTime(),
      [SortAgentsField.Rating]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.average_rating - b.average_rating,
      [SortAgentsField.Download]: (a: AgentMarketPlaceItemWithFilter, b: AgentMarketPlaceItemWithFilter) =>
        a.download_count - b.download_count,
    };

    const sortFunction = sortFunctions[sortAgentsField] || (() => 0);

    return prioritySorted.sort((a, b) => {
      const compareResult = sortFunction(a, b);
      return sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending ? compareResult : -compareResult;
    });
  }, [filteredAgents, sortAgentsField, sortAgentFieldsOrder, getAgentPriority, goldAgentIds]);
  

  const { totalPageCount, paginatedAgents } = useMemo(
    () => ({
      totalPageCount: Math.ceil(sortedAgents.length / itemsPerPage),
      paginatedAgents: sortedAgents.slice((page - 1) * itemsPerPage, page * itemsPerPage),
    }),
    [sortedAgents, page, itemsPerPage]
  );

  return {
    categoryList,
    rolesList,
    cloudProviderList,
    filteredAgents,
    totalPageCount,
    paginatedAgents,
    isAgentDownloadable,
    isAgentChattable,
  };
}

export default useAgentFiltering;
